import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'

export const AUTO_SCALE_MAX_POOL_SIZE = 15

// Optional number input. A blank/empty value means "not set", not 0. The form
// submits empty strings for untouched inputs, so coerce those to undefined
// before validation, else a plain pool errors on auto-scale fields it never used.
const optionalPoolNumber = () =>
  z.preprocess((v) => (v === `` || v === null || v === undefined ? undefined : v), z.coerce.number().optional())

export const dniSchema = z
  .object({
    forwardTo: z.string().min(1, { message: `Select the number this pool replaces` }),
    // Area code is only entered on create (a pool's numbers are already bought on edit).
    areaCode: z
      .string()
      .regex(/^\d{3}$/, { message: `Area code must be exactly 3 digits` })
      .optional(),
    label: z.string().min(1, { message: `A display name is required` }),
    poolSize: z.coerce
      .number()
      .min(1, { message: `Pool size must be at least 1` })
      .max(10, { message: `Pool size cannot exceed 10` }),
    sessionDuration: z.coerce
      .number()
      .min(1, { message: `Session duration must be at least 1 minute` })
      .max(60, { message: `Session duration cannot exceed 60 minutes` })
      .default(30),
    maxPoolSize: optionalPoolNumber(),
    minFree: optionalPoolNumber(),
    buyStep: optionalPoolNumber(),
    minPoolSize: optionalPoolNumber(),
  })
  .refine((v) => v.maxPoolSize === undefined || v.maxPoolSize <= AUTO_SCALE_MAX_POOL_SIZE, {
    message: `Max pool size cannot exceed ${AUTO_SCALE_MAX_POOL_SIZE}`,
    path: [`maxPoolSize`],
  })
  .refine((v) => v.maxPoolSize === undefined || v.maxPoolSize > v.poolSize, {
    message: `Max pool size must be greater than pool size`,
    path: [`maxPoolSize`],
  })
  // minFree/buyStep bounds only apply when auto-scale is on (maxPoolSize set).
  .refine((v) => v.maxPoolSize === undefined || v.minFree === undefined || (v.minFree >= 1 && v.minFree <= 3), {
    message: `Buy when free below must be between 1 and 3`,
    path: [`minFree`],
  })
  .refine((v) => v.maxPoolSize === undefined || v.buyStep === undefined || v.buyStep >= 1, {
    message: `Numbers to buy per step must be at least 1`,
    path: [`buyStep`],
  })
  .refine((v) => v.maxPoolSize === undefined || v.minFree === undefined || v.minFree < v.maxPoolSize, {
    message: `Buy when free below must be less than max pool size`,
    path: [`minFree`],
  })
  .refine((v) => v.maxPoolSize === undefined || v.minPoolSize === undefined || v.minPoolSize >= 1, {
    message: `Min pool size must be at least 1`,
    path: [`minPoolSize`],
  })
  .refine((v) => v.maxPoolSize === undefined || v.minPoolSize === undefined || v.minPoolSize <= v.poolSize, {
    message: `Min pool size cannot exceed the starting pool size`,
    path: [`minPoolSize`],
  })

export const dniValidator = withZod(dniSchema)

/** Extract the 3-digit area code from a phone number, dropping a leading US country code. */
export function extractAreaCode(phone: string): string {
  const digits = phone.replace(/\D/g, ``)
  const national = digits.length === 11 && digits.startsWith(`1`) ? digits.slice(1) : digits
  return national.slice(0, 3)
}
